import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { PricingPlan } from './types';
import { Tag } from '../ui/atoms/Tag';
import { Button } from '@/components/ui/atoms/Button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

/** Savings percentage vs Pay as you go ($15/interview) for each pack */
const PACK_SAVINGS: Record<string, number> = { starter: 5, core: 10, pro: 15 };

interface PricingCardProps {
  plan?: PricingPlan;
  formattedPrice?: string;
  secondaryPriceLabel?: string;
  secondaryCadenceLabel?: string;
  annualCommitmentBadgeLabel?: string;
  priceSubtextOverride?: string;
  isHighlighted?: boolean;
  isCustom?: boolean;
  isSubmitting?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
  // Props for the dropdown variant (landing page specific)
  showPlanSelector?: boolean;
  availablePlans?: PricingPlan[];
  selectedPlanCode?: string;
  onPlanChange?: (value: string) => void;
  buttonText?: string;
  customLabels?: {
    planName: string;
    price: string;
    description: string;
    buttonText: string;
    features: { text: string }[];
  };
  /** GTM tracking attributes for the card click */
  gtmAttributes?: Record<string, string>;
  gtmAttributesSecondary?: Record<string, string>;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  formattedPrice,
  secondaryPriceLabel,
  secondaryCadenceLabel,
  annualCommitmentBadgeLabel,
  priceSubtextOverride,
  isHighlighted = false,
  isCustom = false,
  isSubmitting = false,
  isSelected = false,
  onClick,
  showPlanSelector = false,
  availablePlans = [],
  selectedPlanCode,
  onPlanChange,
  buttonText,
  customLabels,
  gtmAttributes,
  gtmAttributesSecondary,
}) => {
  const { t } = useTranslation('marketing');

  if (!plan && !isCustom) return null;

  // Use gradient background only for highlighted marketing cards, 
  // but for selection state in app, we might want a different style (e.g. border).
  // The guidelines say "Gradient stop 1 for hero backgrounds" etc.
  // We'll stick to the original logic for background but allow overriding via isHighlighted.
  
  const cardBgClass = isHighlighted 
    ? 'bg-[linear-gradient(150deg,var(--color-light-red)_0%,var(--color-light-lin)_40%,var(--color-gradient-light)_50%,var(--color-gradient-purple)_100%)]' 
    : 'bg-[#FCF3ED]';
    
  // If selected in app (not highlighted marketing), maybe we want a border?
  // We can add a border if isSelected is true.
  const selectionClass = isSelected ? 'ring-2 ring-purple shadow-lg' : '';

  const dividerColor = isHighlighted ? '#DCB3FF' : '#E1D2DA';
  const textColor = 'text-dark-invirtus';
  const descriptionColor = isHighlighted ? 'text-dark-invirtus' : 'text-dark-invirtus/70';
  const featureColor = isHighlighted ? 'text-dark-invirtus' : 'text-dark-invirtus/70';

  const effectiveButtonText = buttonText || (isCustom
    ? customLabels?.buttonText ?? t('pricing.custom.button')
    : (isSubmitting ? t('pricing.processing') : plan?.buttonText));
  const primaryCadenceLabel = !isCustom ? (priceSubtextOverride || plan?.priceSubtext) : undefined;

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      whileHover={{ scale: 1.02, y: -5 }}
      className={`flex w-full min-w-0 flex-col items-start flex-[1_0_0] h-full ${cardBgClass} ${selectionClass} p-4 sm:p-6 lg:p-7 rounded-[20px] relative transition-all`}
    >
      {/* Top section: header + button with fixed height so buttons align */}
      <div className="flex flex-col w-full min-h-[280px] sm:min-h-[300px] lg:min-h-[320px] mb-4 sm:mb-5 lg:mb-6">
        {/* Header content */}
        <div className="flex flex-col items-start gap-3 sm:gap-4 w-full flex-grow">
          <div className="w-full flex justify-between items-center min-h-10">
             {showPlanSelector && availablePlans.length > 0 ? (
                 <div className="w-full">
                   <Select value={selectedPlanCode} onValueChange={onPlanChange}>
                    <SelectTrigger className="w-full bg-white/50 border-dark-invirtus/20 font-bold text-dark-invirtus">
                      <SelectValue placeholder={t('pricing.select_plan')} />
                    </SelectTrigger>
                    <SelectContent>
                      {availablePlans.map(p => (
                        <SelectItem key={p.packCode} value={p.packCode} className="font-medium">
                          {p.planName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                   </Select>
                 </div>
             ) : (
                <div className="flex justify-between w-full items-center min-h-10">
                  <h3 className={`w-full ${textColor} text-xl lg:text-2xl font-bold leading-tight`}>
                    {isCustom ? customLabels?.planName ?? t('pricing.custom.name') : plan?.planName}
                  </h3>
                  {isSelected && (
                    <div className="bg-purple rounded-full p-1">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
             )}
          </div>

          <div className="flex flex-wrap items-baseline gap-x-1 gap-y-0 w-full mt-2 min-w-0">
            <div className={`${textColor} font-bold whitespace-nowrap leading-none ${isCustom ? 'w-full min-w-0 text-3xl sm:text-4xl xl:text-[2.45rem]' : 'text-4xl sm:text-5xl xl:text-[2.25rem] 2xl:text-5xl'}`}>
              {isCustom ? customLabels?.price ?? t('pricing.custom.price') : formattedPrice}
            </div>
            {primaryCadenceLabel && (
              <div className={`${textColor} text-sm sm:text-base xl:text-sm 2xl:text-base font-normal leading-5 whitespace-nowrap`}>
                {primaryCadenceLabel}
              </div>
            )}
          </div>
          {secondaryCadenceLabel && (
            <span className="text-sm text-dark-invirtus/70 leading-none">
              {secondaryCadenceLabel}
            </span>
          )}
          {!isCustom && secondaryPriceLabel && (secondaryCadenceLabel ? (
            <div className="flex items-center justify-start gap-2 w-full">
              <span className="inline-flex items-center rounded-md border border-dark-invirtus/25 bg-transparent px-3 py-1 text-xs font-semibold text-dark-invirtus/70 whitespace-nowrap">
                {secondaryPriceLabel} {annualCommitmentBadgeLabel || secondaryCadenceLabel}
              </span>
            </div>
          ) : (
            <div className="mt-1">
              <Tag intent="primary" variant="subtle" className="capitalize">
                {secondaryPriceLabel}
              </Tag>
            </div>
          ))}

          <p className={`w-full ${descriptionColor} ${isHighlighted ? 'font-medium' : 'font-normal'} text-sm leading-5`}>
            {isCustom
              ? customLabels?.description ?? t('pricing.custom.description')
              : plan?.description}
          </p>
          {!isCustom && plan && PACK_SAVINGS[plan.packCode] && (
            <p className="text-xs font-semibold text-green-700">
              {t('pricing.save_vs_payt', { percent: PACK_SAVINGS[plan.packCode] })}
            </p>
          )}
        </div>

        {/* Button - at bottom of fixed-height container */}
        <div className="w-full" {...gtmAttributesSecondary}>
          <Button
            label={effectiveButtonText}
            type={isHighlighted || isCustom ? 'contained' : 'outlined'}
            intent="secondary"
            size="md"
            onClick={onClick}
            loading={!isCustom && isSubmitting}
            className="w-full"
            {...gtmAttributes}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="w-full">
        <svg width="100%" height="2" viewBox="0 0 344 2" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 1H344" stroke={dividerColor} />
        </svg>
      </div>

      {/* Features */}
      <div className="flex flex-col items-start gap-2 w-full mt-4 sm:mt-5 lg:mt-6">
        {(isCustom
          ? customLabels?.features ?? []
          : plan?.features || []).map((feature, index) => (
          <div key={index} className="flex items-start gap-2 w-full">
            <div className="w-5 h-5 flex-shrink-0 mt-0.5">
              <Check className={`w-full h-full ${featureColor}`} />
            </div>
            <span className={`flex-[1_0_0] ${featureColor} text-sm font-normal leading-tight`}>
              {feature.text}
            </span>
          </div>
        ))}
      </div>

    </motion.article>
  );
};
